import { createFileRoute, Link, useNavigate, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type ChangeEvent, type DragEvent } from "react";
import { resolveHandleForLanding } from "@/lib/investor.functions";
import { analyzeDeck } from "@/lib/deck.functions";
import { posthog } from "@/lib/posthog";
import { RESERVED_HANDLES, referralHeadline, REFERRAL_DEFAULT_SUBLINE } from "@/lib/investor-handle";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/$handle")({
  loader: async ({ params }) => {
    const h = params.handle.toLowerCase();
    if (RESERVED_HANDLES.has(h) || h.includes(".")) throw notFound();
    const { investor } = await resolveHandleForLanding({ data: { handle: h } });
    if (!investor) throw notFound();
    return { investor };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "DeckScore" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { investor } = loaderData;
    const url = `https://deckscore.ai/${investor.handle}`;
    const ogImg = `https://deckscore.ai/og/${investor.handle}`;
    const title = referralHeadline(investor.name);
    const desc = investor.invite_blurb?.trim() || REFERRAL_DEFAULT_SUBLINE;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: ogImg },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: ogImg },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BrandedLanding,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center bg-bg text-ink px-6">
      <div className="max-w-md text-center">
        <h1 className="font-display text-3xl font-semibold">Page not found</h1>
        <p className="mt-2 text-ink-secondary">
          This referral link isn't active. Go to the DeckScore home page.
        </p>
        <Link to="/" className="inline-flex mt-6 rounded-full bg-accent text-accent-ink px-5 py-2 text-sm font-bold">
          Go home
        </Link>
      </div>
    </div>
  ),
});

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const s = r.result as string;
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.readAsDataURL(file);
  });
}

function getAnonToken(): string {
  try {
    const k = "deckscore_anon_token";
    let t = localStorage.getItem(k);
    if (!t) { t = crypto.randomUUID(); localStorage.setItem(k, t); }
    return t;
  } catch { return crypto.randomUUID(); }
}

function BrandedLanding() {
  const { investor } = Route.useLoaderData();
  const navigate = useNavigate();
  const analyze = useServerFn(analyzeDeck);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headline = referralHeadline(investor.name);
  const gift = investor.invite_blurb?.trim() || REFERRAL_DEFAULT_SUBLINE;

  const logoUrl = investor.has_logo ? `/og/${investor.handle}/logo` : null;

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type === "application/pdf") setFile(f);
  }
  function onSelect(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }

  async function submit() {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      // Persist ref so downstream flows (claim, delivery) see it.
      try { localStorage.setItem("deckscore_ref", investor.ref_code || investor.handle); } catch { /* noop */ }
      const fileBase64 = await fileToBase64(file);
      const res = await analyze({
        data: {
          fileName: file.name,
          fileBase64,
          mimeType: file.type || "application/pdf",
          anonToken: getAnonToken(),
          ref: investor.ref_code ?? undefined,
        },
      });
      posthog.capture?.("branded_analysis_started", { handle: investor.handle });
      navigate({ to: "/results", search: { deck: res.deckId } as never });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("UNREADABLE_PDF")) {
        setError("We couldn't read any text from this PDF - it looks image-based or scanned. Please upload a text-based PDF (export your slides so the text is selectable).");
      } else if (msg.includes("SCORING_FAILED")) {
        setError("We couldn't score this deck. Please try again in a minute, or upload a different PDF.");
      } else if (msg.includes("FREE_LIMIT")) {
        setError("You've used all 5 free analyses. Submitting to investors has no limit.");
      } else if (msg.includes("RATE_LIMITED")) {
        setError("You've hit today's analysis limit. Please try again tomorrow.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[1120px] flex items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block h-[20px] w-[20px] rounded-md bg-accent" />
            <span className="font-display text-[15px] font-semibold">DeckScore</span>
          </Link>
          <Link to="/for-investors" className="text-[13px] text-ink-secondary hover:text-ink">
            For Investors
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="bg-peach border-b border-line">
          <div className="mx-auto max-w-[880px] px-5 pt-16 pb-14 text-center">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={`${investor.name} logo`}
                className="mx-auto mb-6 h-16 w-16 rounded-2xl object-contain border border-line bg-surface"
              />
            )}
            <p className="font-script text-accent text-[26px] leading-none mb-2">
              invited by <span className="font-display not-italic text-ink font-semibold">{investor.name}</span>
            </p>
            <h1 className="font-display text-[40px] md:text-[52px] font-semibold leading-tight text-ink">
              {headline}
            </h1>
            <p className="mt-5 text-[16px] md:text-[18px] text-ink-secondary max-w-[640px] mx-auto">
              {gift}
            </p>
            <div className="mt-4 text-[13px] text-ink-tertiary">
              No credit card. No pitch - you upload, we score, {investor.name.split(" ")[0]} gets a curated one-pager.
            </div>
          </div>
        </section>


        <section className="mx-auto max-w-[720px] px-5 pt-12 pb-16">
          <div className="bg-surface border border-line rounded-2xl p-6 md:p-8">
            <label
              htmlFor="deck-file"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`block rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition ${
                dragOver ? "border-accent bg-accent/5" : "border-line hover:border-accent/60"
              }`}
            >
              <div className="text-[15px] font-medium">
                {file ? file.name : "Drop your pitch deck PDF here"}
              </div>
              <div className="text-[13px] text-ink-tertiary mt-1">
                or click to browse · PDF only · up to 25 MB
              </div>
              <input
                id="deck-file"
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={onSelect}
              />
            </label>

            {error && (
              <div className="mt-4 rounded-lg border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-[13px]">
                {error}
              </div>
            )}

            <button
              onClick={submit}
              disabled={!file || busy}
              className="mt-5 w-full inline-flex items-center justify-center rounded-full bg-accent text-accent-ink px-5 py-3 text-[15px] font-bold disabled:opacity-60"
            >
              {busy ? "Analyzing…" : `Get my free score & share with ${investor.name.split(" ")[0]} →`}
            </button>
            <p className="mt-3 text-center text-[12px] text-ink-tertiary">
              By continuing, you agree to our{" "}
              <Link to="/terms-and-conditions" className="underline">Terms</Link> and{" "}
              <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
